import { configureStore } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";
import reducer from "./reducer";

// Patterns for filtering sensitive Redux actions
const SENSITIVE_ACTION_PATTERNS = [
  "auth/",
  "login",
  "logout",
  "password",
  "token",
  "payment",
  "card",
];

// Create Sentry Redux enhancer to track actions and state
const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  // Transform actions before sending to Sentry (remove sensitive payloads)
  actionTransformer: (action) => {
    const actionType = action.type?.toLowerCase() || "";

    // Filter sensitive action payloads
    if (SENSITIVE_ACTION_PATTERNS.some((p) => actionType.includes(p))) {
      return {
        ...action,
        payload: "[FILTERED]",
      };
    }

    // Filter any action with potentially sensitive data in payload
    if (action.payload) {
      let payloadStr = "";
      try {
        payloadStr = JSON.stringify(action.payload).toLowerCase();
      } catch {
        return {
          ...action,
          payload: { filtered: true, reason: "unserializable_payload" },
        };
      }
      const sensitiveDataPatterns = [
        "password",
        "token",
        "card",
        "cvv",
        "curp",
        "rfc",
        "clabe",
      ];

      if (sensitiveDataPatterns.some((p) => payloadStr.includes(p))) {
        return {
          ...action,
          payload: { filtered: true, reason: "sensitive_data" },
        };
      }
    }

    return action;
  },

  // Transform state before sending to Sentry (remove sensitive data)
  stateTransformer: (state) => {
    if (!state?.entities) return state;

    // Filter sensitive fields from organization data (third-party credentials)
    let filteredOrganizationData = state.entities.organization?.organizationData;
    if (
      filteredOrganizationData &&
      typeof filteredOrganizationData === "object"
    ) {
      const orgData = filteredOrganizationData as Record<string, unknown>;
      filteredOrganizationData = Object.fromEntries(
        Object.entries(orgData).map(([key, value]) =>
          key.toLowerCase().includes("password") ||
          key.toLowerCase().includes("secret") ||
          key.toLowerCase().includes("credential")
            ? [key, "[FILTERED]"]
            : [key, value],
        ),
      );
    }

    return {
      ...state,
      entities: {
        ...state.entities,
        auth: {
          ...state.entities.auth,
          userToken: state.entities.auth?.userToken ? "[FILTERED]" : null,
        },
        organization: {
          ...state.entities.organization,
          organizationData: filteredOrganizationData,
        },
      },
    };
  },

  // Add Redux state context to Sentry scope
  configureScopeWithState: (scope, state) => {
    if (state?.entities?.user?.userData) {
      const userData = state.entities.user.userData as {
        id?: string | number;
        organization_id?: string | number;
      };
      scope.setTag("user_id", userData.id?.toString() || "unknown");
      scope.setTag(
        "organization_id",
        userData.organization_id?.toString() || "none",
      );
    }
  },
});

export const store = configureStore({
  reducer: reducer,
  devTools: import.meta.env.VITE_APP_ENV !== "production",
  enhancers: (getDefaultEnhancers) =>
    getDefaultEnhancers().concat(sentryReduxEnhancer),
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, user: UsersState}
export type AppDispatch = typeof store.dispatch;
